import { Storage } from "@ionic/storage";
import { storeTherapy } from "../services/storageService";
import {
  AdherenceData,
  ChunkPosition,
  CurveAnalysisData,
  CurveMeasurementFlags,
  CycleData,
  DataPoint,
  MessageType,
  ParsedChunk,
  PressureSetting,
  RealtimeCurvePoint,
  RealtimeData,
  RequestMessageType,
  RequestMessageTypeResult,
  TitrationData,
} from "../contexts/types";
/**
 * Helper function to read a fixed number of bytes and convert to string.
 * @param data number array of deimal int bytes that contain the entire logstring
 * @param start position to start reading
 * @param length length for how much to read
 */
function readFixedBytes(
  data: Uint8Array,
  start: number,
  length: number
): string {
  // Extract the specified number of bytes
  const bytes = Array.from(data.slice(start, start + length)); // Convert to regular array
  // Convert to string, removing unit separators (0x1F)
  return bytes
    .filter((byte) => byte !== 0x1f) // Remove separator bytes
    .map((byte) => String.fromCharCode(byte))
    .join("");
}
/**
 * Helper Function to parse main map fields
 * @param data number array of deimal int bytes that contain the entire logstring
 * @param mainMap main map to be used for reading byte array
 * @param valMap val map to be used for reading Byte array`
 */
// Define an interface for the result object
interface ResultObject {
  [key: string]: string;
}

function parseMainMapFields(data: Uint8Array, mainMap: string, valMap: string) {
  const result: ResultObject = {};
  let position = 0;
  try {
    // Iterate over the mainMap to dynamically process each key-value pair
    JSON.parse(mainMap).OrderedData.forEach((item: { [x: string]: any }) => {
      const key = Object.keys(item)[0]; // Get the key
      const size = Number(item[key]); // Get the size for the current field
      if (key === "VAL") {
        // When VAL is encountered, calculate the size dynamically based on EVT
        const eventType = result["EV_T"] ? result["EV_T"].trim() : ""; // Get EVT value (already read from data)
        const { valResult, valPosition } = parseValField(
          data,
          eventType,
          position,
          valMap
        );
        // console.log("valResult", valResult, "valPosition", valPosition);
        // const valSize = 332;  // This function determines the size based on EVT
        // result[key] = readFixedBytes(data, position, valSize);
        result[key] = JSON.stringify(valResult);
        position = valPosition;
      } else {
        // Read fixed-width fields and add them to the result object
        const bytes = readFixedBytes(data, position, size);
        result[key] = bytes;
        position += size;
      }
    });

    return result;
  } catch (error) {
    console.error("Error parsing main map fields:", error);
    return null; // Return null if parsing fails
  }
}

/**
 * Helper Function to parse val map fields
 * @param valData number array of deimal int bytes that contain the entire logstring
 * @param evenType eventType string based on which a certain "val" value will be picked from valMap
 * @param valMap val map to be used for reading Byte array
 * @param postion postion to start reading byte array from
 */
function parseValField(
  valData: Uint8Array,
  eventType: string,
  position: number,
  valMap: string
) {
  const result: { [key: string]: string } = {};
  // Parse the valMap for the given eventType
  const fields = JSON.parse(valMap)[eventType];
  if (Array.isArray(fields)) {
    fields.forEach((field) => {
      const key = Object.keys(field)[0]; // Get the key
      let size;
      if (field[key].includes("*")) {
        // Handle dependent key with multiplication
        const [dependentKey, multiplier] = field[key].split("*");
        const dependentValue = result[dependentKey]; // Get the dependent key's value
        size = parseInt(multiplier, 10) * Number(dependentValue);

        // Recursively parse sub-fields if the key is nested
        if (JSON.parse(valMap)) {
          const subFields = JSON.parse(valMap)[key];
          for (let i = 0; i < Number(dependentValue); i++) {
            subFields.forEach((subField: { [x: string]: any }) => {
              const subKey = Object.keys(subField)[0];
              const subSize = Number(subField[subKey]);
              result[`${subKey}_${i}`] = readFixedBytes(
                valData,
                position,
                subSize
              );
              position += subSize;
            });
          }
        } else {
          // Read the fixed bytes if no sub-fields exist
          result[key] = readFixedBytes(valData, position, size);
          position += size;
        }
      } else {
        // Handle fixed size fields
        size = Number(field[key]);
        result[key] = readFixedBytes(valData, position, size);
        position += size;
      }
    });
  } else if (Object.keys(fields).length === 1) {
    const key = Object.keys(fields)[0];
    const size = parseInt(JSON.parse(valMap)[eventType][key]);
    result[key] = readFixedBytes(valData, position, size);
    position += size;
  } else {
    console.error("check val map"); // eventype not an array but contains multiple keys
  }

  return { valResult: result, valPosition: position };
}
/**
 * Helper Function to reconstruct logString from recieved bytearray
 * @param data object of allvalues obtained from reading byte array
 * @param refId reference ID to append to the log string
 * @param event_id event ID to append to the log string
 */
function constructString(data: ResultObject, refId: string, event_id: string) {
  const obj = data;
  const result = Object.keys(obj)
    .map((key) => {
      if (key === "VAL") {
        // Handle VAL specially
        const valObj = JSON.parse(obj[key]);
        const valEntries = Object.entries(valObj).map(([subKey, subValue]) => {
          // Map subkeys to uppercase with underscores
          const newKey = subKey.replace(/(_\d+)$/, "");
          return `${newKey}:${subValue}`;
        });
        return `${key}:{${valEntries.join(",")}}`;
      } else {
        // Map main keys
        return `${key}:${obj[key]}`;
      }
    })
    .join("|");

  // Append the additional columns
  return `${result}|EVTID:${event_id}|REFID:${refId}`;
}

export const createLogStringFromByteArray = async (
  byteArray: Uint8Array,
  mainMap: string,
  valMap: string,
  storage: Storage | null,
  event_id: string,
  batch_id: string
) => {
  console.log("createLogstringFromByteArray", event_id, batch_id);

  const resultFinal = parseMainMapFields(byteArray, mainMap, valMap);
  if (!resultFinal) {
    return false;
  }
  // console.log("resultFinal1", resultFinal);
  const reconstructedLogString = constructString(
    resultFinal,
    batch_id,
    event_id
  );
  // console.log("resultFinal2", reconstructedLogString);
  if (batch_id !== "0" && storage) {
    await storeTherapy(storage, reconstructedLogString, event_id, batch_id);
  }
  // console.log("reconstructedLogString", reconstructedLogString);
  return { reconstructedLogString, event_id, batch_id };
};

/**
 * Decode measurement bitmask into human-readable string for debugging
 * @param bitmask The measurement bitmask to decode
 * @returns String representation like "PRESSURE|VOLUME" or "NONE"
 */
export function decodeMeasurementBitmask(bitmask: number): string {
  const measurements: string[] = [];

  if (bitmask & CurveMeasurementFlags.PRESSURE_FLAG)
    measurements.push("PRESSURE");
  if (bitmask & CurveMeasurementFlags.FLOW_FLAG) measurements.push("FLOW");
  if (bitmask & CurveMeasurementFlags.VOLUME_FLAG) measurements.push("VOLUME");

  if (measurements.length === 0) {
    return "NONE";
  }

  return measurements.join("|");
}

/**
 * Parse a chunked BLE message with 9-byte header format
 * @param receivedData Raw bytes received from BLE
 * @returns ParsedChunk with header, payload, and validation status
 */
function parseChunkedMessage(receivedData: Uint8Array): ParsedChunk {
  const result: ParsedChunk = {
    header: {
      messageType: 0,
      chunkPosition: 0,
      chunkNumber: 0,
      totalChunks: 0,
      refIdSize: 0,
      eventIdSize: 0,
      includedMeasurements: 0,
    },
    payload: new Uint8Array(0),
    isValid: false,
  };

  // Minimum header size is now 9 bytes (updated from 8)
  if (receivedData.length < 9) {
    result.errorMessage = "Data too short for valid 9-byte header";
    return result;
  }

  try {
    let offset = 0;

    // Parse fixed header fields (first 9 bytes)
    result.header.messageType = receivedData[offset++];
    result.header.chunkPosition = receivedData[offset++];

    // Parse chunk number (2 bytes, little-endian)
    result.header.chunkNumber =
      receivedData[offset] | (receivedData[offset + 1] << 8);
    offset += 2;

    // Parse total chunks (2 bytes, little-endian)
    result.header.totalChunks =
      receivedData[offset] | (receivedData[offset + 1] << 8);
    offset += 2;

    // Parse variable ID sizes
    result.header.refIdSize = receivedData[offset++];
    result.header.eventIdSize = receivedData[offset++];

    // Parse measurement bitmask (NEW in 9-byte header)
    result.header.includedMeasurements = receivedData[offset++];

    // Validate header values
    if (!Object.values(MessageType).includes(result.header.messageType)) {
      result.errorMessage = `Invalid message type: ${result.header.messageType}`;
      return result;
    }

    if (!Object.values(ChunkPosition).includes(result.header.chunkPosition)) {
      result.errorMessage = `Invalid chunk position: ${result.header.chunkPosition}`;
      return result;
    }

    if (result.header.chunkNumber >= result.header.totalChunks) {
      result.errorMessage = `Invalid chunk number: ${result.header.chunkNumber} >= ${result.header.totalChunks}`;
      return result;
    }

    // Validate measurement bitmask for curve messages
    if (
      result.header.messageType === MessageType.CURVES ||
      result.header.messageType === MessageType.REALTIME_CURVES
    ) {
      if (result.header.includedMeasurements === 0) {
        result.errorMessage =
          "CURVES/REALTIME_CURVES messages must include at least one measurement";
        return result;
      }
    } else {
      // For non-curves messages, measurement bitmask should be 0
      if (result.header.includedMeasurements !== 0) {
        console.warn(
          "⚠️ Non-curves message with non-zero measurement bitmask:",
          result.header.includedMeasurements
        );
      }
    }

    // Validate ID sizes are reasonable (0-8 bytes each)
    if (result.header.refIdSize > 8) {
      result.errorMessage = `Invalid refIdSize: ${result.header.refIdSize} (max 8)`;
      return result;
    }
    if (result.header.eventIdSize > 8) {
      result.errorMessage = `Invalid eventIdSize: ${result.header.eventIdSize} (max 8)`;
      return result;
    }

    // Parse variable-length IDs if present
    if (result.header.refIdSize > 0) {
      if (offset + result.header.refIdSize > receivedData.length) {
        result.errorMessage = "Data too short for refId";
        return result;
      }

      result.header.refId = 0;
      for (let i = 0; i < result.header.refIdSize; i++) {
        result.header.refId |= receivedData[offset + i] << (i * 8);
      }
      offset += result.header.refIdSize;
    }

    if (result.header.eventIdSize > 0) {
      if (offset + result.header.eventIdSize > receivedData.length) {
        result.errorMessage = "Data too short for eventId";
        return result;
      }

      result.header.eventId = 0;
      for (let i = 0; i < result.header.eventIdSize; i++) {
        result.header.eventId |= receivedData[offset + i] << (i * 8);
      }
      offset += result.header.eventIdSize;
    }

    // Extract payload (remaining bytes)
    if (offset < receivedData.length) {
      result.payload = receivedData.slice(offset);
    }

    result.isValid = true;
    return result;
  } catch (error) {
    result.errorMessage = `Parsing error: ${error}`;
    return result;
  }
}

/**
 * Get human-readable message type name
 * @param messageType Numeric message type
 * @returns String representation of message type
 */
function getMessageTypeName(messageType: number): string {
  switch (messageType) {
    case MessageType.LOGS:
      return "LOGS";
    case MessageType.ADHERENCE:
      return "ADHERENCE";
    case MessageType.TITRATION:
      return "TITRATION";
    case MessageType.CURVES:
      return "CURVES";
    case MessageType.MAIN_MAP:
      return "MAIN_MAP";
    case MessageType.VAL_MAP:
      return "VAL_MAP";
    case MessageType.UPDATED_CURVES_LIST:
      return "UPDATED_CURVES_LIST";
    case MessageType.UPDATED_TITRATION_SUMMARY:
      return "UPDATED_TITRATION_SUMMARY";
    case MessageType.REALTIME_CURVES:
      return "REALTIME_CURVES";
    default:
      return `UNKNOWN(${messageType})`;
  }
}

/**
 * Get human-readable chunk position name
 * @param position Numeric chunk position
 * @returns String representation of chunk position
 */
function getChunkPositionName(position: number): string {
  switch (position) {
    case ChunkPosition.MIDDLE:
      return "MIDDLE";
    case ChunkPosition.START:
      return "START";
    case ChunkPosition.END:
      return "END";
    case ChunkPosition.SINGLE:
      return "SINGLE";
    default:
      return `UNKNOWN(${position})`;
  }
}

/**
 * Main parsing function with detailed logging and measurement decoding
 * @param receivedData Raw bytes received from BLE
 * @returns ParsedChunk with complete header and payload information
 */
export function parseAndLogChunk(receivedData: Uint8Array): ParsedChunk {
  const parsed = parseChunkedMessage(receivedData);

  if (parsed.isValid) {
    console.log("✅ Chunk parsed successfully:");
    console.log(
      `📦 Message Type: ${getMessageTypeName(parsed.header.messageType)}`
    );
    console.log(
      `📍 Position: ${getChunkPositionName(parsed.header.chunkPosition)}`
    );
    console.log(
      `🔢 Chunk: ${parsed.header.chunkNumber + 1}/${parsed.header.totalChunks}`
    );

    // Show measurement information for curves messages
    if (
      parsed.header.messageType === MessageType.CURVES ||
      parsed.header.messageType === MessageType.REALTIME_CURVES
    ) {
      console.log(
        `🎯 Included measurements: ${decodeMeasurementBitmask(
          parsed.header.includedMeasurements
        )} ` +
          `(0x${parsed.header.includedMeasurements
            .toString(16)
            .padStart(2, "0")
            .toUpperCase()})`
      );
    } else if (parsed.header.includedMeasurements !== 0) {
      console.log(
        `📝 Non-CURVES message - measurements bitmask: ${parsed.header.includedMeasurements} (should be 0)`
      );
    }

    // Show IDs if present
    if (parsed.header.refId !== undefined) {
      console.log(`🆔 Ref ID: ${parsed.header.refId}`);
    }
    if (parsed.header.eventId !== undefined) {
      console.log(`🎯 Event ID: ${parsed.header.eventId}`);
    }

    console.log(`📊 Payload Size: ${parsed.payload.length} bytes`);

    // Log first few bytes of payload for debugging
    if (parsed.payload.length > 0) {
      const previewBytes = Math.min(20, parsed.payload.length);
      const preview = Array.from(parsed.payload.slice(0, previewBytes))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(" ");
      console.log(
        `🔍 Payload Preview: ${preview}${
          parsed.payload.length > previewBytes ? "..." : ""
        }`
      );
    }
  } else {
    console.error("❌ Failed to parse chunk:", parsed.errorMessage);
  }

  return parsed;
}

/**
 * Utility function to extract just the header information without logging
 * @param receivedData Raw bytes received from BLE
 * @returns ParsedChunk with header information
 */
export function parseChunkSilent(receivedData: Uint8Array): ParsedChunk {
  return parseChunkedMessage(receivedData);
}

/**
 * Check if a message type supports curve measurements
 * @param messageType The message type to check
 * @returns True if message type supports measurements
 */
export function supportsMeasurements(messageType: number): boolean {
  return (
    messageType === MessageType.CURVES ||
    messageType === MessageType.REALTIME_CURVES
  );
}

/**
 * Validate that a measurement bitmask is valid for the given message type
 * @param messageType The message type
 * @param bitmask The measurement bitmask
 * @returns True if valid, false otherwise
 */
export function isValidMeasurementBitmask(
  messageType: number,
  bitmask: number
): boolean {
  if (supportsMeasurements(messageType)) {
    // Curves messages must have at least one measurement
    return bitmask > 0 && bitmask <= 0x07; // Only bits 0-2 are valid currently
  } else {
    // Non-curves messages should have bitmask = 0
    return bitmask === 0;
  }
}

// Example usage function
function example() {
  // Simulate received BLE data (replace with actual BLE characteristic value)
  const mockChunkData = new Uint8Array([
    0x02, // Message Type: ADHERENCE
    0x01, // Position: START
    0x00,
    0x00, // Chunk Number: 0
    0x05,
    0x00, // Total Chunks: 5
    0x00, // Ref ID Size: 0
    0x00, // Event ID Size: 0
    // Payload follows...
    0x5b,
    0x32,
    0x30,
    0x2c,
    0x31,
    0x30,
    0x2c,
    0x36,
    0x30, // "[20,10,60"
  ]);

  const result = parseAndLogChunk(mockChunkData);

  if (result.isValid) {
    // Convert payload to string for adherence data
    const payloadText = new TextDecoder().decode(result.payload);
    console.log(`📝 Payload as text: "${payloadText}"`);
  }
}

export function mergeChunks(chunks: Uint8Array[]): Uint8Array {
  const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }
  return merged;
}

// export function parseAdherenceFromText(rawText: string): AdherenceData {
//   console.log("Parsing adherence data from text:", rawText);
//   const parsed = JSON.parse(rawText);
//   const [therapyDays, missedDays, avgAdherence, ...sessionEntries] = parsed;

//   const sessions: AdherenceData["sessions"] = [];

//   for (const entry of sessionEntries) {
//     const [date, entryValue] = Object.entries(entry)[0] as [
//       string,
//       [number, number[]]
//     ];
//     const [cycleCount, slotCounts] = entryValue;

//     const timeSlots = {
//       "12AM": slotCounts[0],
//       "6AM": slotCounts[1],
//       "12PM": slotCounts[2],
//       "6PM": slotCounts[3],
//     };

//     sessions.push({ date, timeSlots, cycleCount });
//   }

//   return {
//     stats: { therapyDays, missedDays, avgAdherence },
//     sessions,
//   };
// }

// Enhanced parsing function that can handle partial data efficiently
export function parseAdherenceFromText(
  rawText: string,
  isPartial = false
): AdherenceData | null {
  console.log("Parsing adherence data from text:", rawText);

  try {
    // Method 1: Try complete JSON parsing first (fastest for complete data)
    if (!isPartial) {
      const parsed = JSON.parse(rawText);
      return buildAdherenceData(parsed, false);
    }

    // Method 2: Progressive parsing for partial data
    return parsePartialAdherence(rawText);
  } catch (error) {
    console.log("Standard JSON parsing failed, attempting partial parsing...");

    // Fallback: Try partial parsing even if isPartial was false
    return parsePartialAdherence(rawText);
  }
}

// Core function to build adherence data from parsed JSON array
function buildAdherenceData(parsed: any[], isPartial: boolean): AdherenceData {
  const [therapyDays, missedDays, avgAdherence, ...sessionEntries] = parsed;

  const sessions = sessionEntries.map((entry) => {
    const [date, entryValue] = Object.entries(entry)[0] as [
      string,
      [number, number[]]
    ];
    const [cycleCount, slotCounts] = entryValue;

    return {
      date,
      cycleCount,
      timeSlots: {
        "12AM": slotCounts[0],
        "6AM": slotCounts[1],
        "12PM": slotCounts[2],
        "6PM": slotCounts[3],
      },
    };
  });

  return {
    stats: { therapyDays, missedDays, avgAdherence },
    sessions,
  };
}

// Sophisticated partial parsing that identifies complete day objects
function parsePartialAdherence(rawText: string): AdherenceData | null {
  console.log(`Attempting partial parse on ${rawText.length} characters`);

  // Step 1: Extract header (stats) using regex
  // Looking for pattern: [number,number,number,
  const headerMatch = rawText.match(/^\[(\d+),(\d+),(\d+),/);
  if (!headerMatch) {
    console.log("❌ No valid header found");
    return null;
  }

  const [, therapyDays, missedDays, avgAdherence] = headerMatch.map(Number);
  console.log(
    `✅ Header found: therapy=${therapyDays}, missed=${missedDays}, avg=${avgAdherence}`
  );

  // Step 2: Find complete day objects
  const completeDays = findCompleteDayObjects(rawText);
  console.log(`✅ Found ${completeDays.length} complete day objects`);

  if (completeDays.length === 0) {
    console.log("❌ No complete day objects found");
    return null;
  }

  // Step 3: Reconstruct valid JSON with found days
  const reconstructedJson = `[${therapyDays},${missedDays},${avgAdherence},${completeDays.join(
    ","
  )}]`;
  console.log(`✅ Reconstructed JSON with ${completeDays.length} days`);

  try {
    const parsed = JSON.parse(reconstructedJson);
    return buildAdherenceData(parsed, true);
  } catch (error) {
    console.log("❌ Failed to parse reconstructed JSON:", error);
    return null;
  }
}

// Advanced function to identify complete day objects in partial JSON
function findCompleteDayObjects(text: string): string[] {
  const completeDays: string[] = [];
  let position = 0;

  console.log("🔍 Scanning for complete day objects...");

  while (position < text.length) {
    // Look for start of day object: {"YYYY-MM-DD"
    const dayStartPattern = /\{"(\d{4}-\d{2}-\d{2})"/g;
    dayStartPattern.lastIndex = position;

    const startMatch = dayStartPattern.exec(text);
    if (!startMatch) {
      console.log(`🔍 No more day starts found from position ${position}`);
      break;
    }

    const dayStart = startMatch.index;
    const date = startMatch[1];
    console.log(`🔍 Found day start at position ${dayStart} for date: ${date}`);

    // Find the complete day object by counting brackets and handling strings
    const dayObject = extractCompleteJsonObject(text, dayStart);

    if (dayObject) {
      completeDays.push(dayObject);
      console.log(`✅ Complete day object extracted for ${date}: ${dayObject}`);
      // Move position past this object
      position = dayStart + dayObject.length;
    } else {
      console.log(`❌ Incomplete day object for ${date}, stopping extraction`);
      break;
    }
  }

  return completeDays;
}

// Precise function to extract a complete JSON object starting at a given position
function extractCompleteJsonObject(
  text: string,
  startPos: number
): string | null {
  let bracketCount = 0;
  let inString = false;
  let escapeNext = false;
  let pos = startPos;

  // Track the object boundaries
  while (pos < text.length) {
    const char = text[pos];

    if (escapeNext) {
      escapeNext = false;
      pos++;
      continue;
    }

    if (char === "\\") {
      escapeNext = true;
      pos++;
      continue;
    }

    if (char === '"') {
      inString = !inString;
    } else if (!inString) {
      if (char === "{") {
        bracketCount++;
      } else if (char === "}") {
        bracketCount--;

        // When bracket count reaches 0, we've found the complete object
        if (bracketCount === 0) {
          const completeObject = text.substring(startPos, pos + 1);
          console.log(
            `🎯 Complete object found: brackets closed at position ${pos}`
          );

          // Validate it's a proper day object
          if (isValidDayObject(completeObject)) {
            return completeObject;
          } else {
            console.log(`❌ Invalid day object structure: ${completeObject}`);
            return null;
          }
        }
      }
    }

    pos++;
  }

  // If we reach here, the object is incomplete
  console.log(
    `❌ Object incomplete: bracket count = ${bracketCount}, reached end of text`
  );
  return null;
}

// Validation function to ensure extracted object is a valid day entry
function isValidDayObject(objectStr: string): boolean {
  try {
    const parsed = JSON.parse(objectStr);

    // Must be an object with exactly one key-value pair
    const entries = Object.entries(parsed);
    if (entries.length !== 1) return false;

    const [date, value] = entries[0];

    // Date must be in YYYY-MM-DD format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;

    // Value must be an array with 2 elements: [number, [number, number, number, number]]
    if (!Array.isArray(value) || value.length !== 2) return false;

    const [cycleCount, timeSlots] = value;
    if (typeof cycleCount !== "number") return false;
    if (!Array.isArray(timeSlots) || timeSlots.length !== 4) return false;
    if (!timeSlots.every((slot) => typeof slot === "number")) return false;

    console.log(`✅ Valid day object: ${date} with ${cycleCount} cycles`);
    return true;
  } catch (error) {
    console.log(`❌ Day object validation failed: ${error}`);
    return false;
  }
}

export function parseBLETitrationData(mergedChunks: Uint8Array): TitrationData {
  try {
    console.log("Parsing BLE titration data...", mergedChunks);
    // Decode the merged chunks to text
    const jsonText = new TextDecoder().decode(mergedChunks);
    console.log("Raw JSON text:", jsonText);
    // Parse the JSON array
    const data = JSON.parse(jsonText) as [string, number[][], number[][]];
    console.log("Raw parsed data:", data);
    if (!Array.isArray(data) || data.length < 2) {
      throw new Error(
        "Invalid data format: Expected array with at least 2 elements"
      );
    }

    const [isoDateString, measurementArrays, conditionalArrays = []] = data;

    // Format the date
    const therapyDate = formatTherapyDate(isoDateString);
    console.log("Parsed therapy date:", therapyDate);

    // Process cycles data
    const cycles = processCycles(measurementArrays);
    console.log("Parsed cycles:", cycles);

    // Process pressure settings
    const pressureSettings = processPressureSettings(conditionalArrays);
    console.log("Parsed pressure settings:", pressureSettings);

    return {
      therapyDate,
      cycles,
      pressureSettings,
    };
  } catch (error) {
    console.error("Error parsing BLE therapy data:", error);
    throw new Error(
      `Failed to parse therapy data: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

export function formatTherapyDate(isoString: string): string {
  try {
    const date = new Date(isoString);

    // Format: "24 March 2025 ( 5:45:05 PM )"
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "long" });
    const year = date.getFullYear();
    const time = date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    return `${day} ${month} ${year} ( ${time} )`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return isoString; // Fallback to original string
  }
}

function processCycles(measurementArrays: number[][]): CycleData[] {
  const cycles: CycleData[] = [];

  // Separate cycles by feedback type
  const preferredCycles: number[][] = [];
  const dislikedCycles: number[][] = [];
  const noneCycles: number[][] = [];

  // Sort cycles by feedback type (last element: 0=none, 1=preferred, 2=disliked)
  measurementArrays.forEach((measurement) => {
    if (measurement.length >= 5) {
      const feedbackFlag = measurement[4];
      switch (feedbackFlag) {
        case 1:
          preferredCycles.push(measurement);
          break;
        case 2:
          noneCycles.push(measurement);
          break;
        default:
          dislikedCycles.push(measurement);
          break;
      }
    }
  });

  let cycleId = 1;

  // Add preferred cycles (up to 4, but could be more)
  preferredCycles.forEach((measurement) => {
    cycles.push({
      id: cycleId++,
      pause: measurement[1], // value2 (0-15)
      inspiration: measurement[2], // value3 (0-70)
      expiration: measurement[3], // value4 (-70 to 0)
      feedback: "preferred",
    });
  });

  // Add none cycles
  noneCycles.forEach((measurement) => {
    cycles.push({
      id: cycleId++,
      pause: measurement[1],
      inspiration: measurement[2],
      expiration: measurement[3],
      feedback: "none",
    });
  });

  // Add disliked cycles
  dislikedCycles.forEach((measurement) => {
    cycles.push({
      id: cycleId++,
      pause: measurement[1],
      inspiration: measurement[2],
      expiration: measurement[3],
      feedback: "disliked",
    });
  });

  return cycles;
}

function processPressureSettings(
  conditionalArrays: number[][]
): PressureSetting[] {
  const labels = [
    "Min pressure cm H2O",
    "Max pressure cm H2O",
    "Avg pressure cm H2O",
    "Avg duration Sec",
  ];

  const pressureSettings: PressureSetting[] = [];

  // Process up to 4 arrays or create default values if empty
  for (let i = 0; i < 4; i++) {
    const array = conditionalArrays[i];
    const label = labels[i];

    if (array && array.length >= 3) {
      pressureSettings.push({
        label,
        pause: Number(array[0].toFixed(1)),
        insp: Number(array[1].toFixed(1)),
        exp: Number(array[2].toFixed(1)),
      });
    } else {
      // Provide default values if array is missing
      pressureSettings.push({
        label,
        pause: 0,
        insp: 0,
        exp: 0,
      });
    }
  }

  return pressureSettings;
}

/**
 * Decode Updated Titration Summary message (returns ISO datetime string)
 * Input: merged payload Uint8Array (header already extracted)
 */
export function decodeUpdatedTitrationSummary(
  payload: Uint8Array
): Array<[string, string]> {
  // Convert Uint8Array directly to string (no JSON parsing needed)
  const decoder = new TextDecoder("utf-8");
  const jsonString = decoder.decode(payload);
  try {
    const parsedArray = JSON.parse(jsonString) as Array<[string, string]>;
    return parsedArray;
  } catch (error) {
    throw new Error(`Failed to parse updated Titration list JSON: ${error}`);
  }
}

/**
 * Decode Updated Curves List message (returns array of [datetime, refId] pairs)
 * Input: merged payload Uint8Array (header already extracted)
 */
export function decodeUpdatedCurvesList(
  payload: Uint8Array
): Array<[number, string, number]> {
  // Convert Uint8Array to string and parse as JSON
  const decoder = new TextDecoder("utf-8");
  const jsonString = decoder.decode(payload);

  try {
    const parsedArray = JSON.parse(jsonString) as Array<
      [number, string, number]
    >;
    return parsedArray;
  } catch (error) {
    throw new Error(`Failed to parse curves list JSON: ${error}`);
  }
}

/**
 * Process raw curve data from BLE characteristic
 * Input: rawData - array of [timeMs, pressure, flow, volume] tuples
 * Output: CurveAnalysisData object with three arrays of DataPoint
 */

function processRawData(
  rawData: Array<[number | null, number | null, number | null, number | null]>
): CurveAnalysisData {
  console.log(`Processing ${rawData.length} curve data points...`);
  console.log("Raw data sample:", rawData.slice(0, 5));

  // Check if time has undefined values - if so, return all empty
  const hasUndefinedTime = rawData.some(([timeMs]) => timeMs === undefined);

  if (hasUndefinedTime) {
    console.log("Found undefined time values, returning all empty arrays");
    return {
      flowData: [],
      pressureData: [],
      volumeData: [],
    };
  }

  // Check which specific fields have undefined values
  const hasUndefinedPressure = rawData.some(
    ([, pressure, ,]) => pressure === undefined
  );
  const hasUndefinedFlow = rawData.some(([, , flow]) => flow === undefined);
  const hasUndefinedVolume = rawData.some(
    ([, , , volume]) => volume === undefined
  );
  console.log(
    "Undefined fields:",
    `Pressure: ${hasUndefinedPressure}`,
    `Flow: ${hasUndefinedFlow}`,
    `Volume: ${hasUndefinedVolume}`
  );

  // Initialize arrays for the three data types
  const flowData: DataPoint[] = [];
  const pressureData: DataPoint[] = [];
  const volumeData: DataPoint[] = [];

  // Process each data point
  for (const [timeMs, pressure, flow, volume] of rawData) {
    const timeInSeconds = timeMs as number; // Safe since we checked time isn't undefined

    // Only add flow data if no undefined flow values exist
    if (!hasUndefinedFlow) {
      flowData.push({
        time: timeInSeconds,
        value: flow as number,
      });
    }

    // Only add pressure data if no undefined pressure values exist
    if (!hasUndefinedPressure) {
      pressureData.push({
        time: timeInSeconds,
        value: pressure as number,
      });
    }

    // Only add volume data if no undefined volume values exist
    if (!hasUndefinedVolume) {
      volumeData.push({
        time: timeInSeconds,
        value: volume as number,
      });
    }
  }

  console.log("Curve data processed:", {
    flowDataLength: flowData.length,
    pressureDataLength: pressureData.length,
    volumeDataLength: volumeData.length,
    timeRange:
      flowData.length > 0
        ? `${flowData[0]?.time}s to ${flowData[flowData.length - 1]?.time}s`
        : "N/A",
  });

  return {
    flowData,
    pressureData,
    volumeData,
  };
}
export function decodeCurveData(
  payload: Uint8Array,
  includedMeasurements: number
): CurveAnalysisData {
  console.log("=== STARTING CURVE DATA DECOMPRESSION ===");
  console.log("📦 Binary payload length:", payload.length);

  if (payload.length < 12) {
    throw new Error("❌ Payload too small for 12-byte header");
  }

  const view = new DataView(
    payload.buffer,
    payload.byteOffset,
    payload.byteLength
  );

  console.log("📋 Reading header...");

  let pressure = false;
  let flow = false;
  let volume = false;
  let expectedSizeForSingleDataPoint = 4;

  const includedMeasurementsString =
    decodeMeasurementBitmask(includedMeasurements);

  if (includedMeasurementsString.includes("PRESSURE")) {
    pressure = true;
    expectedSizeForSingleDataPoint += 2; // Pressure is 2 bytes
  }
  if (includedMeasurementsString.includes("FLOW")) {
    flow = true;
    expectedSizeForSingleDataPoint += 2; // Flow is 2 bytes
  }
  if (includedMeasurementsString.includes("VOLUME")) {
    volume = true;
    expectedSizeForSingleDataPoint += 4; // Volume is 4 bytes
  }

  // Read header (exactly matching Qt write order)
  const baseTimestamp = view.getUint32(0, true); // 4 bytes, little-endian
  const sampleCount = view.getUint32(4, true); // 4 bytes, little-endian
  const version = view.getUint8(8); // 1 byte
  const reserved1 = view.getUint8(9); // 1 byte
  const reserved2 = view.getUint8(10); // 1 byte
  const reserved3 = view.getUint8(11); // 1 byte

  console.log("✅ Header decoded:");
  console.log(
    "  - baseTimestamp:",
    baseTimestamp,
    `(0x${baseTimestamp.toString(16)})`
  );
  console.log("  - sampleCount:", sampleCount);
  console.log("  - version:", version);
  console.log(
    "  - reserved: [",
    reserved1,
    ",",
    reserved2,
    ",",
    reserved3,
    "]"
  );

  // Convert base timestamp back to readable format
  const baseDate = new Date(baseTimestamp);
  console.log("🕐 Base timestamp:", baseDate.toISOString());

  if (version !== 1) {
    throw new Error(`❌ Unsupported version: ${version} (expected 1)`);
  }

  // Size verification
  const expectedSize = 12 + sampleCount * expectedSizeForSingleDataPoint; // 12-byte header + 12 bytes per sample
  console.log("📏 Size verification:");
  console.log("  - Header size: 12 bytes");
  console.log("  - Sample count:", sampleCount);
  console.log("  - Bytes per sample:", expectedSizeForSingleDataPoint, "bytes");
  console.log("  - Expected total:", expectedSize, "bytes");
  console.log("  - Actual total:", payload.length, "bytes");
  console.log(
    "  - Match:",
    payload.length === expectedSize ? "✅ YES" : "❌ NO"
  );

  if (payload.length !== expectedSize) {
    const difference = payload.length - expectedSize;
    console.warn("❌ SIZE MISMATCH! Difference:", difference, "bytes");
    console.warn(
      "📊 Calculated sample size:",
      expectedSizeForSingleDataPoint,
      "bytes"
    );
  }

  // Show first 32 bytes as hex for verification
  console.log("🔍 First 32 bytes (hex):");
  const preview = payload.slice(0, 32);
  let hexString = "";
  for (let i = 0; i < preview.length; i++) {
    if (i > 0 && i % 4 === 0) hexString += " ";
    hexString += preview[i].toString(16).padStart(2, "0");
  }
  console.log("   ", hexString);

  const rawData: Array<[number, number | null, number | null, number | null]> =
    [];

  console.log("📖 Reading", sampleCount, "samples...");

  // Read samples (exactly matching Qt write order)
  let samplesRead = 0;
  for (let i = 0; i < sampleCount; i++) {
    const offset = 12 + i * expectedSizeForSingleDataPoint; // 12-byte header + 12 bytes per sample

    if (offset + 12 > payload.length) {
      console.warn(
        `⚠️ Sample ${i} would extend beyond payload, stopping at ${samplesRead} samples`
      );
      break;
    }

    let pressureRaw = null;
    let flowRaw = null;
    let volumeRaw = null;
    let sizeOffset = 0;
    // Read sample data (exactly matching Qt write order)
    const deltaTime = view.getUint32(offset, true); // 4 bytes
    sizeOffset += 4; // Move offset by 4 bytes
    if (pressure) {
      pressureRaw = view.getInt16(offset + sizeOffset, true) as number;
      sizeOffset += 2; // Move offset by 2 bytes
    }
    if (flow) {
      flowRaw = view.getInt16(offset + sizeOffset, true) as number; // 2 bytes
      sizeOffset += 2; // Move offset by 2 bytes
    }
    if (volume) {
      volumeRaw = view.getInt32(offset + sizeOffset, true) as number; // 4 bytes
      sizeOffset += 4; // Move offset by 4 bytes
    }
    console.log(
      `Sample ${i}: offset=${offset}, deltaTime=${deltaTime}, pressureRaw=${pressureRaw}, flowRaw=${flowRaw}, volumeRaw=${volumeRaw}`
    );
    // Convert back to original values (reverse of Qt compression)
    const timeMs = deltaTime; // Already in milliseconds (relative to base)
    const pressureVal = pressureRaw !== null ? pressureRaw / 10.0 : null; // Divide by 10 (was multiplied by 10)
    const flowVal = flowRaw !== null ? flowRaw / 10.0 : null; // Divide by 10 (was multiplied by 10)
    const volumeVal = volumeRaw !== null ? volumeRaw / 10.0 : null; // No conversion needed

    rawData.push([timeMs, pressureVal, flowVal, volumeVal]);
    samplesRead++;

    // Debug first few samples
    if (i < 3) {
      console.log(
        `✅ Sample ${i}: bytes ${offset}-${
          offset + 9
        } - time=${deltaTime}ms, pressure=${pressure}→${pressureVal}, flow=${flow}→${flowVal}, volume=${volume}→${volumeVal}`
      );
    }
  }

  console.log("✅ Successfully read", samplesRead, "samples");

  if (samplesRead === 0) {
    throw new Error("❌ No samples were successfully read");
  }

  // Analyze data ranges
  console.log("📊 Data analysis:");

  const times = rawData
    .map((d) => d[0])
    .filter((t) => t !== undefined) as number[];
  const pressures = rawData
    .map((d) => d[1])
    .filter((p) => p !== undefined) as number[];
  const flows = rawData
    .map((d) => d[2])
    .filter((f) => f !== undefined) as number[];
  const volumes = rawData
    .map((d) => d[3])
    .filter((v) => v !== undefined) as number[];

  const timeRange = [Math.min(...times), Math.max(...times)];
  const pressureRange =
    pressures.length > 0
      ? [Math.min(...pressures), Math.max(...pressures)]
      : [0, 0];
  const flowRange =
    flows.length > 0 ? [Math.min(...flows), Math.max(...flows)] : [0, 0];
  const volumeRange =
    volumes.length > 0 ? [Math.min(...volumes), Math.max(...volumes)] : [0, 0];

  console.log(
    `  - Time range: ${timeRange[0]}ms to ${timeRange[1]}ms (${
      (timeRange[1] - timeRange[0]) / 1000
    }s duration)`
  );
  console.log(`  - Pressure range: ${pressureRange[0]} to ${pressureRange[1]}`);
  console.log(`  - Flow range: ${flowRange[0]} to ${flowRange[1]}`);
  console.log(`  - Volume range: ${volumeRange[0]} to ${volumeRange[1]}`);

  console.log("🔄 Processing into final format...");

  // Use existing processRawData function
  const result = processRawData(rawData);

  console.log("=== DECOMPRESSION COMPLETE ===");
  console.log("✅ Final result:", {
    flowDataLength: result.flowData?.length || 0,
    pressureDataLength: result.pressureData?.length || 0,
    volumeDataLength: result.volumeData?.length || 0,
    timeRange: `${timeRange[0] / 1000}s to ${timeRange[1] / 1000}s`,
  });

  return result;
}

/**
 * Decodes a single 12-byte curve point from payload
 *
 * @param payload - Uint8Array containing exactly 12 bytes
 * @param offset - Byte offset to start reading from (default: 0)
 * @returns RealtimeCurvePoint or null if invalid
 */
const decodeSinglePoint = (
  payload: Uint8Array,
  offset = 0,
  size: number,
  pressure: boolean,
  flow: boolean,
  volume: boolean
): RealtimeCurvePoint | null => {
  if (payload.length < offset + size) {
    return null;
  }

  const dataView = new DataView(
    payload.buffer,
    payload.byteOffset + offset,
    size
  );

  // Read little-endian values (true = little-endian)
  let pressureRaw = null;
  let flowRaw = null;
  let volumeRaw = null;
  let expectedSize = 0; // For elapsedTimeMs (4 bytes)
  const elapsedTimeMs = dataView.getUint32(0, true); // 4 bytes (unsigned)
  expectedSize += 4;
  if (pressure) {
    pressureRaw = dataView.getInt16(expectedSize, true);
    expectedSize += 2; // 2 bytes (signed)
  }
  if (flow) {
    flowRaw = dataView.getInt16(expectedSize, true);
    expectedSize += 2; // 2 bytes (signed)
  }
  if (volume) {
    volumeRaw = dataView.getInt32(expectedSize, true);
    expectedSize += 4; // 4 bytes (signed)
  }
  // console.log(
  //   `Decoded point at offset ${offset}: elapsedTimeMs=${elapsedTimeMs}, pressureRaw=${pressureRaw}, flowRaw=${flowRaw}, volumeRaw=${volumeRaw}`
  // );
  // Apply scaling (divide by 10 to get original values)
  return {
    elapsedTimeMs,
    pressure: pressureRaw !== null ? pressureRaw / 10.0 : undefined,
    flow: flowRaw !== null ? flowRaw / 10.0 : undefined,
    volume: volumeRaw !== null ? volumeRaw / 10.0 : undefined,
  };
};

/**
 * Calculates number of data points in payload
 *
 * @param payload - Uint8Array containing the raw payload data
 * @returns Number of complete 12-byte points, or -1 if invalid
 */
const getPointCount = (payload: Uint8Array, size: number): number => {
  if (payload.length === 0 || payload.length % size !== 0) {
    return -1;
  }
  return payload.length / size;
};

/**
 * Validates if payload size is correct for curve data
 *
 * @param payload - Uint8Array to validate
 * @returns true if payload size is valid (multiple of 12 bytes)
 */
const isValidPayloadSize = (
  payload: Uint8Array,
  pressure: boolean,
  flow: boolean,
  volume: boolean
): boolean => {
  let size = 4; // 4 bytes for elapsedTimeMs
  if (pressure) size += 2; // 2 bytes for pressure
  if (flow) size += 2; // 2 bytes for flow
  if (volume) size += 4; // 4 bytes for volume
  // console.log(
  //   `Validating payload size: ${payload.length} bytes, expected size multiple of ${size}`
  // );
  return payload.length > 0 && payload.length % size === 0;
};

/**
 * Decodes real-time curve data payload into array of data points
 * Pure functional approach - no side effects
 *
 * @param payload - Uint8Array containing the raw payload data (without BLE header)
 * @returns Array of RealtimeCurvePoint objects, empty array if invalid
 */
const decodeRealtimeCurvePayload = (
  payload: Uint8Array,
  pressure: boolean,
  flow: boolean,
  volume: boolean
): RealtimeCurvePoint[] => {
  // Validate payload
  if (!isValidPayloadSize(payload, pressure, flow, volume)) {
    console.log("❌ Invalid payload size:", payload.length);
    return [];
  }
  let size = 4; // 4 bytes for elapsedTimeMs
  if (pressure) size += 2; // 2 bytes for pressure
  if (flow) size += 2; // 2 bytes for flow
  if (volume) size += 4; // 4 bytes for volume
  const pointCount = getPointCount(payload, size);
  console.log("📊 Number of points to decode:", pointCount);
  if (pointCount <= 0) {
    return [];
  }

  // Decode all points using functional approach
  return Array.from({ length: pointCount }, (_, index) => {
    const offset = index * size;
    return decodeSinglePoint(payload, offset, size, pressure, flow, volume);
  }).filter((point): point is RealtimeCurvePoint => point !== null);
};

/**
 * Validates a single curve point for reasonable medical device ranges
 *
 * @param point - RealtimeCurvePoint to validate
 * @returns true if point values are within expected ranges
 */
const isValidPoint = (point: RealtimeCurvePoint): boolean => {
  let isValid = point.elapsedTimeMs >= 0 && point.elapsedTimeMs <= 3600000;
  if (point.pressure) {
    isValid = isValid && point.pressure >= -150.0 && point.pressure <= 150.0; // qint16 range / 10
  }
  if (point.flow) {
    isValid = isValid && point.flow >= -900.0 && point.flow <= 900.0; // qint16 range / 10
  }
  if (point.volume) {
    isValid = isValid && point.volume >= -50000.0 && point.volume <= 50000.0; // reasonable volume range
  }
  return isValid;
};

/**
 * Validates all points in an array
 *
 * @param points - Array of RealtimeCurvePoint to validate
 * @returns true if all points are valid
 */
const validateAllPoints = (points: RealtimeCurvePoint[]): boolean => {
  return points.length > 0 && points.every(isValidPoint);
};

/**
 * Filters out invalid points from an array
 *
 * @param points - Array of RealtimeCurvePoint to filter
 * @returns Array containing only valid points
 */
const filterValidPoints = (
  points: RealtimeCurvePoint[]
): RealtimeCurvePoint[] => {
  return points.filter(isValidPoint);
};

/**
 * Main decoder function - takes payload, returns validated data points
 *
 * @param payload - Uint8Array containing the raw payload data (without BLE header)
 * @returns Array of valid RealtimeCurvePoint objects
 */
export const decodeAndValidatePayload = (
  payload: Uint8Array,
  pressure: boolean,
  flow: boolean,
  volume: boolean
): RealtimeCurvePoint[] => {
  const points = decodeRealtimeCurvePayload(payload, pressure, flow, volume);
  // console.log("📊 Decoded points:", points);
  const validPoints = filterValidPoints(points);
  // console.log("✅ Valid points:", validPoints);
  return validPoints;
};

/**
 * Helper function to convert points to simple objects (for JSON serialization)
 *
 * @param points - Array of RealtimeCurvePoint
 * @returns Array of plain objects
 */
const pointsToJSON = (points: RealtimeCurvePoint[]) => {
  return points.map((point) => ({
    time: point.elapsedTimeMs,
    pressure: point.pressure ? Number(point.pressure.toFixed(1)) : undefined,
    flow: point.flow ? Number(point.flow.toFixed(1)) : undefined,
    volume: point.volume ? Number(point.volume.toFixed(1)) : undefined,
  }));
};

/**
 * Converts RealtimeCurvePoint array to RealtimeData format
 *
 * @param points - Array of RealtimeCurvePoint to convert
 * @returns RealtimeData with separate arrays for each measurement type
 */
export const convertToRealtimeData = (
  points: RealtimeCurvePoint[]
): RealtimeData => {
  const flowData: DataPoint[] = [];
  const pressureData: DataPoint[] = [];
  const volumeData: DataPoint[] = [];

  points.forEach((point) => {
    const time = point.elapsedTimeMs;

    if (point.flow != null) flowData.push({ time, value: point.flow });
    if (point.pressure != null)
      pressureData.push({ time, value: point.pressure });
    if (point.volume != null) volumeData.push({ time, value: point.volume });
  });

  return {
    flowData,
    pressureData,
    volumeData,
  };
};

/**
 * Generates a 16-bit message type request for BLE communication
 * @param messageTypes Array of message types to enable (must contain at least one element)
 * @returns Object containing the API string and debug information
 *
 * @example
 * // For remote control screen in auto mode (bits 6 and 8 set)
 * const result = generateMessageTypeRequest([MessageType.MIE_DATA, MessageType.PRESSURE_GAUGE]);
 * console.log(result.apiString); // "MsgTypeReq,320" (binary: 0000000101000000)
 *
 * @example
 * // For curves analysis screen (bit 5 set)
 * const result = generateMessageTypeRequest([MessageType.REALTIME_CURVES]);
 * console.log(result.apiString); // "MsgTypeReq,32" (binary: 0000000000100000)
 */
export function generateMessageTypeRequest(
  messageTypes: RequestMessageType[] // At least one element required
): RequestMessageTypeResult {
  // Validate message types and separate valid from invalid
  const requestedTypes: string[] = [];
  const validTypes: RequestMessageType[] = [];
  const invalidTypes: string[] = [];
  const bitPositions: number[] = [];

  messageTypes.forEach((type) => {
    const typeName = RequestMessageType[type];
    requestedTypes.push(typeName || `Unknown(${type})`);

    // Check if the bit position is within 16-bit range (0-15)
    if (typeof type === "number" && type >= 0 && type <= 15 && typeName) {
      validTypes.push(type);
      bitPositions.push(type);
    } else {
      invalidTypes.push(typeName || `Unknown(${type})`);
    }
  });

  // Generate the 16-bit value using bitwise OR operations
  let messageValue = 0;
  validTypes.forEach((type) => {
    messageValue |= 1 << type; // Set the bit at position 'type'
  });

  // Generate binary representation for debugging (16-bit padded)
  const binaryRepresentation = messageValue.toString(2).padStart(16, "0");

  // Create the API string
  const apiString = `MsgTypeReq,${messageValue}`;

  // Prepare debug information
  const debugInfo = {
    requestedTypes,
    validTypes: validTypes.map((type) => RequestMessageType[type]),
    invalidTypes,
    binaryRepresentation,
    decimalValue: messageValue,
    bitPositions: bitPositions.sort((a, b) => a - b),
  };

  return {
    apiString,
    debugInfo,
  };
}

// Mock Data Generation Function
export const createRealTimeMockDataGenerator = (
  durationMinutes: number,
  onDataUpdate: (data: RealtimeData) => void
) => {
  const startTime = Date.now();
  let currentData: RealtimeData = {
    flowData: [],
    pressureData: [],
    volumeData: [],
  };

  const dataInterval = 30; // 30ms between data points
  const maxDuration = durationMinutes * 60 * 1000; // Convert to milliseconds

  const generatePoint = (
    elapsedMs: number,
    type: "flow" | "pressure" | "volume"
  ): number => {
    const timeInSeconds = elapsedMs / 1000;

    switch (type) {
      case "flow": {
        // Flow: -900 to 900 LPM with breathing pattern
        const flowBase = Math.sin(timeInSeconds * 0.5) * 400;
        const flowVariation = Math.sin(timeInSeconds * 2) * 100;
        const flowNoise = (Math.random() - 0.5) * 50;
        return Number((flowBase + flowVariation + flowNoise).toFixed(1));
      }
      case "pressure": {
        // Pressure: -150 to 150 cm H2O with pressure cycles
        const pressureBase = Math.sin(timeInSeconds * 0.3) * 60;
        const pressureVariation = Math.cos(timeInSeconds * 1.5) * 20;
        const pressureNoise = (Math.random() - 0.5) * 10;
        return Number(
          (pressureBase + pressureVariation + pressureNoise).toFixed(1)
        );
      }
      case "volume": {
        // Volume: -10,000 to 10,000 mL with tidal volume pattern
        const volumeBase = Math.sin(timeInSeconds * 0.25) * 4000;
        const volumeVariation = Math.sin(timeInSeconds * 0.8) * 1000;
        const volumeNoise = (Math.random() - 0.5) * 200;
        return Number((volumeBase + volumeVariation + volumeNoise).toFixed(1));
      }
      default:
        return 0;
    }
  };

  const interval = setInterval(() => {
    const elapsed = Date.now() - startTime;

    if (elapsed >= maxDuration) {
      clearInterval(interval);
      return;
    }

    // Add new data points
    const newFlowPoint: DataPoint = {
      time: elapsed,
      value: generatePoint(elapsed, "flow"),
    };

    const newPressurePoint: DataPoint = {
      time: elapsed,
      value: generatePoint(elapsed, "pressure"),
    };

    const newVolumePoint: DataPoint = {
      time: elapsed,
      value: generatePoint(elapsed, "volume"),
    };

    currentData = {
      flowData: [...currentData.flowData, newFlowPoint],
      pressureData: [...currentData.pressureData, newPressurePoint],
      volumeData: [...currentData.volumeData, newVolumePoint],
    };

    onDataUpdate(currentData);
  }, dataInterval);

  return () => clearInterval(interval);
};
